import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Dispatch } from 'redux';

import * as UsuarioService from '../../../services/UsuarioService';
import { clearLinkAccessSession, PENDING_LINK_KEY, startLinkAccessSession } from '../../../utils/linkAccessSession';
import { ClientCompleteData, dispatchClientData, getInitialRoute, saveUserSession, setUserLogProperties, UserInformation } from '../../../utils/userSession';
import { consumeAccessLink } from '../services';
import { LinkAccessErrorStatus, LinkAccessStatus } from '../types';

const COLLABORATOR_SESSION_KEYS = ['autenticacao.token', 'usuario', 'cliente'];

const ERROR_STATUS_BY_HTTP_STATUS: Record<number, LinkAccessErrorStatus> = {
  404: 'notFound',
  403: 'forbidden',
  409: 'unavailable',
};

const getErrorStatus = (error: unknown): LinkAccessErrorStatus => {
  const httpStatus = (error as { response?: { status?: number } } | undefined)?.response?.status;
  return (httpStatus && ERROR_STATUS_BY_HTTP_STATUS[httpStatus]) || 'failure';
};

const readLinkKey = () => {
  const keyFromHash = window.location.hash.replace(/^#/, '');
  if (window.location.href.includes('#')) {
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
  }
  return keyFromHash || sessionStorage.getItem(PENDING_LINK_KEY) || '';
};

const snapshotCollaboratorSession = () => COLLABORATOR_SESSION_KEYS.map(key => [key, localStorage.getItem(key)] as const);

const restoreCollaboratorSession = (snapshot: ReturnType<typeof snapshotCollaboratorSession>) => {
  snapshot.forEach(([key, value]) => (value === null ? localStorage.removeItem(key) : localStorage.setItem(key, value)));
};

const enterWithLinkToken = async (linkKey: string, collaboratorToken: string, dispatch: Dispatch, history: ReturnType<typeof useHistory>) => {
  const linkToken = await consumeAccessLink(linkKey, collaboratorToken);
  const collaboratorSession = snapshotCollaboratorSession();
  localStorage.setItem('autenticacao.token', linkToken.access_token);
  startLinkAccessSession(linkToken);

  try {
    const userResponse: { data: unknown } = await UsuarioService.getUsuarioInformation(linkToken.access_token);
    const userInformation = userResponse.data as UserInformation;
    const clientData: ClientCompleteData | null = await UsuarioService.getClienteCompletoByToken(userInformation.usuario.login, linkToken.access_token);
    if (!clientData) throw new Error('Cliente do técnico não encontrado');

    saveUserSession(userInformation, clientData, linkToken.access_token);
    dispatchClientData(dispatch, clientData);
    setUserLogProperties(clientData);
    history.replace(getInitialRoute(clientData) ?? '/login');
  } catch (error) {
    restoreCollaboratorSession(collaboratorSession);
    clearLinkAccessSession();
    throw error;
  }
};

export const useLinkAccess = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [status, setStatus] = useState<LinkAccessStatus>('loading');
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const linkKey = readLinkKey();
    if (!linkKey) {
      setStatus('invalid');
      return;
    }

    const collaboratorToken = localStorage.getItem('autenticacao.token');
    if (!collaboratorToken || collaboratorToken === 'null') {
      sessionStorage.setItem(PENDING_LINK_KEY, linkKey);
      history.replace({ pathname: '/login', state: { from: '/link-acesso' } });
      return;
    }

    sessionStorage.removeItem(PENDING_LINK_KEY);
    enterWithLinkToken(linkKey, collaboratorToken, dispatch, history).catch(error => setStatus(getErrorStatus(error)));
  }, [dispatch, history]);

  return status;
};
